'use client';

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { QuizStore, QuizConfig, QuizQuestion, QuizAnswerState } from '@/types';

export const useQuizStore = create<QuizStore>()(
  immer((set, get) => ({
    config: null,
    questions: [],
    currentIndex: 0,
    answers: {},
    startTime: null,
    questionStartTime: null,
    streakCount: 0,
    isComplete: false,

    initQuiz: (config: QuizConfig, questions: QuizQuestion[]) => {
      set((state) => {
        state.config = config;
        state.questions = questions;
        state.currentIndex = 0;
        state.answers = {};
        state.startTime = Date.now();
        state.questionStartTime = Date.now();
        state.streakCount = 0;
        state.isComplete = false;
      });
    },

    answerQuestion: (questionId: string, selectedOptionId: string) => {
      const { questions } = get();
      const question = questions.find((q) => q.questionId === questionId);
      if (!question) return;

      const isCorrect = selectedOptionId === question.correctOptionId;
      const answerState: QuizAnswerState = isCorrect ? 'correct' : 'wrong';

      set((state) => {
        state.answers[questionId] = { selectedId: selectedOptionId, state: answerState };
        if (isCorrect) {
          state.streakCount += 1;
        } else {
          state.streakCount = 0;
        }
      });
    },

    timeoutQuestion: (questionId: string) => {
      set((state) => {
        if (!state.answers[questionId]) {
          state.answers[questionId] = { selectedId: '', state: 'timeout' };
        }
        state.streakCount = 0;
      });
    },

    nextQuestion: () => {
      const { currentIndex, questions } = get();
      set((state) => {
        state.questionStartTime = Date.now();
        if (currentIndex < questions.length - 1) {
          state.currentIndex = currentIndex + 1;
        } else {
          state.isComplete = true;
        }
      });
    },

    completeQuiz: () => {
      set((state) => {
        state.isComplete = true;
      });
    },

    resetQuiz: () => {
      set((state) => {
        state.config = null;
        state.questions = [];
        state.currentIndex = 0;
        state.answers = {};
        state.startTime = null;
        state.questionStartTime = null;
        state.streakCount = 0;
        state.isComplete = false;
      });
    },

    getCurrentQuestion: () => {
      const { questions, currentIndex } = get();
      return questions[currentIndex] ?? null;
    },

    getScore: () => {
      const { questions, answers } = get();
      const total = questions.length;
      const correct = questions.filter(
        (q) => answers[q.questionId]?.state === 'correct'
      ).length;
      const wrong = total - correct;
      const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
      return { correct, wrong, total, percentage };
    },

    getXPEarned: () => {
      const { questions, answers } = get();
      return questions.reduce((total, q) => {
        const answer = answers[q.questionId];
        if (answer?.state === 'correct') {
          return total + q.xpReward;
        }
        return total;
      }, 0);
    },
  }))
);
